import React, { useState } from 'react';
import PropTypes from 'prop-types';

import ChevronDown from '@decisiv/iconix/lib/components/ChevronDown';
import ChevronUp from '@decisiv/iconix/lib/components/ChevronUp';
import DropdownList from '@decisiv/ui-components/lib/components/DropdownList';
import Tooltip from '@decisiv/ui-components/lib/components/Tooltip';

import roleTag from '../../../shared/roleTag';
import EMPTY_VALUE from '../../../shared/emptyValue';
import { LockedControl, TIER_OPTIONS_PORTAL_ID } from './styles';

/**
 * The MGMT. ROLE cell: the tier held in this account's management role group, as
 * its coloured tag, with the other tiers behind a chevron. An em dash when the
 * group is off and there is nothing to choose between.
 *
 * Built on KeyDS DropdownList for the listbox keyboard behaviour a hand-wired
 * Popover-plus-OptionsList did not have. `multiple` is left off, so it reports
 * one id. Visibility is controlled rather than left to DropdownList so the
 * chevron can track it.
 *
 * The trigger is one Tag with an `action`, which draws the divided pill itself,
 * so the tier reads as the same tag it is everywhere else on the page.
 *
 * `containerSelector` is load-bearing: DropdownList forwards no className, so
 * the element it portals into is the only ancestor its options can be styled
 * from. PermissionsMatrix mounts that element once, outside the table's scroll
 * container — see TierOptionsPortal.
 */
function ManagementRoleCell({
  disabled,
  label,
  lockReason,
  onSelect,
  options,
  value,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const Chevron = isOpen ? ChevronUp : ChevronDown;

  if (!value) {
    return <span aria-label={`${label} — none`}>{EMPTY_VALUE}</span>;
  }

  if (disabled) {
    const tag = roleTag(value.label);

    if (!lockReason) {
      return tag;
    }

    return (
      <Tooltip
        placement="top"
        target={
          <LockedControl aria-label={`${label} — ${lockReason}`} tabIndex={0}>
            {tag}
          </LockedControl>
        }
      >
        {lockReason}
      </Tooltip>
    );
  }

  return (
    <DropdownList
      containerSelector={`#${TIER_OPTIONS_PORTAL_ID}`}
      items={options.map((option) => ({
        id: option.roleId,
        label: option.label,
      }))}
      minWidth={160}
      name={label}
      // Always an array, even for a single selection.
      onChange={([roleId]) => {
        onSelect(roleId);
        setIsOpen(false);
      }}
      onHide={() => setIsOpen(false)}
      placement="bottom-start"
      renderOptionLabel={(item) => roleTag(item.label)}
      selectedIds={value.roleId}
      showArrow={false}
      target={roleTag(value.label, {
        action: () => setIsOpen((open) => !open),
        actionIcon: Chevron,
        actionAttributes: {
          'aria-expanded': isOpen,
          'aria-haspopup': 'listbox',
          'aria-label': label,
          role: 'button',
        },
      })}
      visible={isOpen}
      // The portal container is mounted before the table in document order, so
      // without this the panel paints underneath the rows. One is enough — it
      // only has to clear its siblings, not the app chrome.
      zIndex={1}
    />
  );
}

ManagementRoleCell.propTypes = {
  disabled: PropTypes.bool,
  /** Accessible name — "Mgmt. Role for Amazon Logistics". */
  label: PropTypes.string.isRequired,
  lockReason: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string, roleId: PropTypes.string }),
  ),
  /** The tier currently held, or null when the group is off. */
  value: PropTypes.shape({ label: PropTypes.string, roleId: PropTypes.string }),
};

ManagementRoleCell.defaultProps = {
  disabled: false,
  lockReason: null,
  options: [],
  value: null,
};

export default ManagementRoleCell;
